import { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { setUser, setMobileDashboard, setLoader } from 'redux/reducers';
import { useCreateDispatches } from 'helpers/hooks';
import * as services from 'services';
import * as helpers from 'helpers';
import * as utils from './utils/settingsUtils';
import * as Comp from 'components';
import MultiFactor from '../MultiFactor/MultiFactor';
import ChildToggles from './components/ChildToggles';
import LogoutButton from './components/LogoutButton';
import NotificationToggles from './components/NotificationToggles';
import AccountImages from './components/AccountImages';
import ExportPdfButton from 'components/Child/components/ChildAdmin/ExportPdf/ExportPdfButton';
import ExportPdfForm from 'components/Child/components/ChildAdmin/ExportPdf/ExportPdfForm';
import styles from './account.module.css';
import useManageAccountImage from './utils/useManageAccountImage';
import moment from 'moment';

function OwnerProfile(props) {
	const { user, setMobileDashboard, history } = props;
	const { setSnackbar, setLoader } = useCreateDispatches();

	const [privatize, setPrivatize] = useState(Boolean(user.privatizePosts));
	const [open, setOpen] = useState(false);

	const infoState = utils.useInfoState();
	const notifications = utils.useNotificationToggles(user);
	const handleValidate = utils.useValidate();
	const imageState = useManageAccountImage();

	const cutoffDate = moment().subtract(13, 'years').toDate();
	const dobFormat = moment(user.dob).toDate();
	const meetsAgeRequirement = moment(dobFormat).isSameOrBefore(cutoffDate);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const setProfileFromProps = useCallback(async (user) => {
		//set user with the onChange function from useFormInput hook
		infoState.firstname.onChange({ target: { value: user.firstname || '' } });
		infoState.lastname.onChange({ target: { value: user.lastname || '' } });
		infoState.email.onChange({ target: { value: user.email || '' } });
		infoState.mobile.onChange({
			target: {
				value: user.mobile ? helpers.handlePhoneInput(user.mobile) : '',
			},
		});
		setPrivatize(user.privatizePosts);
	});

	useEffect(() => {
		setProfileFromProps(user);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const submitRequest = async () => {
		const { firstname, lastname, email, mobile, oldPassword, newPassword } =
			infoState || {};

		const notificationBody = helpers.includeType(['agencyuser'])
			? utils.notificationReqBuilder(notifications)
			: {};

		const reqBody = {
			firstname: firstname.value,
			lastname: lastname.value,
			email: email.value,
			mobile: mobile.value === undefined ? '' : mobile.value,
			...notificationBody,
		};

		if (props.user?.role !== 'admin') {
			reqBody.role = undefined;
			reqBody.branchId = undefined;
		}

		if (newPassword.value) {
			reqBody.password = oldPassword.value;
			reqBody.newPassword = newPassword.value;
		}

		if (imageState.file?.file) {
			reqBody.photo = await services.getLocationAndUpload(
				imageState.file.file,
				user.id,
			);
		} else if (!imageState.photo) {
			reqBody.photo = '';
		}

		const promiseArray = [];
		if (helpers.includeType(['child'])) {
			if (helpers.experiments.v2Api(true)) {
				if (user.privatizePosts !== privatize) {
					reqBody.privatizePosts = privatize;
				}
			}
		}

		promiseArray.push(services.updateUser(reqBody));

		Promise.all(promiseArray)
			.then(() => {
				if (infoState.oldPassword?.value) {
					infoState.oldPassword.onChange({
						target: { value: '' },
					});
					infoState.newPassword.onChange({
						target: { value: '' },
					});
					infoState.newPwConfirm.onChange({
						target: { value: '' },
					});
				}
				services.getOwnUser('fetchUpdate');
				setSnackbar({
					variant: 'success',
					message: 'Profile Updated Successfully',
				});
				setLoader(false);
			})
			.catch((error) => {
				error.response?.data?.message &&
					setSnackbar({
						variant: 'error',
						message: error.response.data.message,
					});
				setLoader(false);
			});
	};

	const onSubmit = () => {
		if (handleValidate(infoState)) {
			props.setLoader(true);
			submitRequest();
		}
	};

	const onCancel = () => {
		history.goBack();
	};

	const onMobileNavBack = () => {
		setMobileDashboard(true);
	};

	const handleSetOpen = () => {
		setOpen(!open);
	};

	let buttonArray = [
		<Comp.SecondaryButton key='account-cancel' onClick={onCancel}>
			Cancel
		</Comp.SecondaryButton>,
		<Comp.PrimaryButton key='account-save' onClick={onSubmit}>
			Save
		</Comp.PrimaryButton>,
		<LogoutButton key='account-logout' />,
	];
	return (
		<Comp.PageLayout
			title='Edit Profile'
			navBack={onMobileNavBack}
			buttonArray={buttonArray}>
			<div className={styles.settingsContent}>
				<AccountImages {...{ ...imageState }} />
				<Comp.ProfileBasics
					infoState={infoState}
					user={user}
					onSubmit={onSubmit}
				/>
				{helpers.includeActiveChild() && helpers.excludeArchivedChild() && (
					<div className={styles.childOuterWrapper}>
						<div className={styles.childWrapper}>
							<ChildToggles {...{ privatize, setPrivatize }} />
							{meetsAgeRequirement ? (
								<>
									<div className={styles.childAdminItemWrapper}>
										<div>
											<div className={styles.childAdminBoldLabel}>
												Use this option to export a copy of your LifeBook.
											</div>
											<div>Export Lifebook PDF</div>
										</div>
										<div className={styles.childAdminButtonContainer}>
											{!open && <ExportPdfButton setOpen={handleSetOpen} />}
										</div>
									</div>
									{open && <ExportPdfForm type={'pdf'} setOpen={setOpen} />}
								</>
							) : null}
						</div>
					</div>
				)}
				{helpers.includeType([
					'agencyuser',
					'casauser',
					'csw',
					'resource-parent',
					'contributor',
				]) && <MultiFactor />}
				{helpers.includeType(['agencyuser']) && (
					<NotificationToggles notifications={notifications} />
				)}
			</div>
		</Comp.PageLayout>
	);
}

const mapStateToProps = ({ user }) => {
	return {
		user: user.user,
	};
};

const mapDispatchToProps = {
	setUser,
	setMobileDashboard,
	setLoader,
};

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(OwnerProfile),
);
